import { Text } from "@clipboard-health/ui-components";
import { Button, Card, CardContent, CircularProgress, Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useToast } from "@src/appV2/lib";
import axios from "axios";

import type {
  CognitoAuthentication,
  CognitoUser,
} from "../../../../lib/cognito/src/lib/amplifyAuth";
import { environmentConfig } from "../../../environment";

interface SessionApiTestProps {
  cognitoAuth: CognitoAuthentication;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  setResult: (result: string) => void;
  setApiResult: (result: string) => void;
  currentUser: CognitoUser | undefined;
  setCurrentUser: (user: CognitoUser | undefined) => void;
}

export function SessionApiTest(props: SessionApiTestProps) {
  const {
    cognitoAuth,
    isLoading,
    setIsLoading,
    setResult,
    setApiResult,
    currentUser,
    setCurrentUser,
  } = props;

  const { showErrorToast } = useToast();

  const handleGetCurrentUser = async () => {
    setIsLoading(true);
    setResult("");
    setApiResult("");

    try {
      const user = await cognitoAuth.getCurrentUserInfo();
      if (user) {
        setResult(`Current user:\n${JSON.stringify(user, null, 2)}`);
      } else {
        showErrorToast("No user currently signed in");
      }
    } catch (error) {
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? (error.message as string)
          : "An unknown error occurred";
      showErrorToast(`Error getting current user: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    setResult("");
    setApiResult("");

    try {
      await cognitoAuth.signOut();
      setCurrentUser(undefined);
    } catch (error) {
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? (error.message as string)
          : "An unknown error occurred";
      showErrorToast(`Error signing out: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestApiWithCognitoToken = async () => {
    setIsLoading(true);
    setResult("");
    setApiResult("");

    try {
      const token = await cognitoAuth.getIdToken();
      if (!token) {
        setApiResult("Error: No ID token found. Please sign in with magic link first.");
        return;
      }

      const url = `${environmentConfig.REACT_APP_BASE_API_URL}/user/info`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

      setApiResult(
        `API Response (Status: ${response.status}):\n${JSON.stringify(response.data, null, 2)}`
      );
    } catch (error) {
      let errorMessage = "An unknown error occurred";

      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? "N/A";
        const data: unknown = error.response?.data;
        const message: string =
          data && typeof data === "object" && "message" in data && typeof data.message === "string"
            ? data.message
            : "";
        errorMessage = `API Error (${status}): ${message || "Request failed"}`;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = error.message as string;
      }

      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Text
          variant="h6"
          sx={(theme: Theme) => ({
            marginBottom: theme.spacing(3),
          })}
        >
          Session & API Testing
        </Text>
        <Stack
          sx={(theme: Theme) => ({
            gap: theme.spacing(1),
          })}
        >
          {currentUser && (
            <Button
              fullWidth
              variant="outlined"
              disabled={isLoading}
              onClick={handleGetCurrentUser}
            >
              {isLoading ? <CircularProgress size={24} /> : "Get Current User"}
            </Button>
          )}
          {currentUser && (
            <Button
              fullWidth
              variant="outlined"
              disabled={isLoading}
              onClick={handleTestApiWithCognitoToken}
            >
              {isLoading ? <CircularProgress size={24} /> : "Test API with Cognito Token"}
            </Button>
          )}
          {currentUser && (
            <Button
              fullWidth
              variant="contained"
              color="warning"
              disabled={isLoading}
              onClick={handleSignOut}
            >
              {isLoading ? <CircularProgress size={24} /> : "Sign Out"}
            </Button>
          )}
          {!currentUser && (
            <Button
              fullWidth
              variant="outlined"
              disabled={isLoading}
              onClick={handleGetCurrentUser}
            >
              {isLoading ? <CircularProgress size={24} /> : "Get Current User"}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
